import PropTypes from 'prop-types';
import React from 'react';

import { AthenaDepartment } from 'common-src/models/PatientIntegrationsAthena';

import { VISIT_TYPES } from '../../constants';
import { daysUntil, getPatient, isTaskReadinessComplete } from '../../helpers';
import styles from './AppointmentCard.module.scss';

const getTaskReadinessStatus = (task) => {
  if (isTaskReadinessComplete(task)) return 'green';
  if (task?.priority === 'critical') return 'red';
  return 'yellow';
};

const getOverallCardStatus = (tasks = []) => {
  const statuses = tasks.map(getTaskReadinessStatus);
  if (statuses.includes('red')) return 'red';
  if (statuses.includes('yellow')) return 'yellow';
  if (statuses.includes('green')) return 'green';
  return 'neutral';
};

const READINESS_STATUS_ORDER = {
  green: 0,
  yellow: 1,
  red: 2,
};

const departmentNameById = Object.values(AthenaDepartment).reduce((acc, department) => {
  acc[`${department.id}`] = department.value;
  return acc;
}, {});

const formatDepartmentLabel = (departmentAthenaId) => {
  const departmentName = departmentNameById[`${departmentAthenaId ?? ''}`];
  if (!departmentName) return '';

  return departmentName.replace(/^Neurology\s+/i, '').trim();
};

const AppointmentCard = ({ appt, onClick, isSelected, appointmentTasks = [] }) => {
  const patient = appt?.patient ?? getPatient(appt?.patientId);
  const visitType = VISIT_TYPES[appt.visitType];
  const workflowStageLabel = `${appt?.workflowStageLabel || ''}`.trim();
  const departmentLabel = formatDepartmentLabel(appt?.departmentAthenaId);
  const days = appt?.date ? daysUntil(appt.date) : null;
  const hasValidDays = Number.isFinite(days);
  const totalTaskCount = appointmentTasks.length;
  const completedTaskCount = appointmentTasks.filter((task) =>
    isTaskReadinessComplete(task),
  ).length;
  const outstandingTaskCount = totalTaskCount - completedTaskCount;
  const overallCardStatus = getOverallCardStatus(appointmentTasks);
  const readinessBlocks = appointmentTasks
    .map((task) => ({
      id: task.id,
      status: getTaskReadinessStatus(task),
    }))
    .sort((a, b) => READINESS_STATUS_ORDER[a.status] - READINESS_STATUS_ORDER[b.status]);
  const dateLabel = hasValidDays
    ? days === 0
      ? 'Today'
      : days === 1
        ? 'Tomorrow'
        : days < 0
          ? `${Math.abs(days)}d ago`
          : `in ${days}d`
    : '—';
  const isSoon = hasValidDays && days <= 2;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.card} ${isSelected ? styles.selected : ''}`}
      style={{
        '--card-border':
          overallCardStatus === 'green'
            ? '#bbf7d0'
            : overallCardStatus === 'yellow'
              ? '#fde68a'
              : overallCardStatus === 'red'
                ? '#fecaca'
                : '#e4e4e7',
        '--card-left':
          overallCardStatus === 'green'
            ? '#22c55e'
            : overallCardStatus === 'yellow'
              ? '#eab308'
              : overallCardStatus === 'red'
                ? '#ef4444'
                : '#d4d4d8',
      }}
    >
      <div className={styles.header}>
        <div>
          <div className={styles.patientName}>{patient?.name}</div>
          <div className={styles.visitMeta}>
            <span>
              {workflowStageLabel
                ? `${workflowStageLabel} · ${visitType?.short || appt.visitType}`
                : `Upcoming ${visitType?.short || appt.visitType} visit`}
            </span>
            {departmentLabel ? (
              <>
                <span className={styles.metaDivider}>·</span>
                <span className={styles.department}>{departmentLabel}</span>
              </>
            ) : null}
          </div>
        </div>
        <div className={styles.dateBlock}>
          <div className={`${styles.dateLabel} ${isSoon ? styles.dateSoon : styles.dateNormal}`}>
            {dateLabel}
          </div>
          <div className={styles.timeLabel}>{appt.time}</div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.readinessRow}>
          <span className={styles.readinessLabel}>Readiness</span>
          <div className={styles.readinessBlocks}>
            {readinessBlocks.map((block) => (
              <span
                key={block.id}
                className={`${styles.readinessBlock} ${styles[`readinessBlock${block.status}`]}`}
              />
            ))}
          </div>
          <span className={styles.readinessCount}>{`${completedTaskCount}/${totalTaskCount}`}</span>
        </div>
        {outstandingTaskCount > 0 ? (
          <div className={styles.taskPill}>
            {outstandingTaskCount} outstanding task{outstandingTaskCount === 1 ? '' : 's'}
          </div>
        ) : totalTaskCount > 0 ? (
          <div className={styles.taskPillDone}>Ready</div>
        ) : (
          <div className={styles.taskPillMuted}>No linked tasks</div>
        )}
      </div>
    </button>
  );
};

AppointmentCard.propTypes = {
  appt: PropTypes.shape({
    patient: PropTypes.shape({
      name: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    visitType: PropTypes.string,
    workflowStageLabel: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    departmentAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
  appointmentTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.number,
      priority: PropTypes.string,
    }),
  ),
};

export default AppointmentCard;
